import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Fade, Tooltip } from "@mui/material";
import tailwindClassnames from "../common/TailwindClassnames";
import ConfirmationModal from "../common/ConfirmationModal";
import InfoTooltip from "../common/InfoTooltip";
import Pagination from "../common/Pagination";
import { EditPencilIcon, DeleteXIcon, InfoIcon } from "../common/ProjectIcons";
import ProjectTaxonPicker from "./ProjectTaxonPicker";
import EditMappingModal, { EditMappingMode } from "./EditMappingModal";
import AddSpeciesModal from "./AddSpeciesModal";
import { pageSize } from "../../api/apiUtils";
import {
  addProjectMapping,
  bulkResetProjectMappings,
  bulkUpsertProjectMappings,
  deleteProjectMapping,
  removeSpeciesListItem,
  updateProjectMapping,
} from "../../api/projectsApi";
import {
  AiMappingsRow,
  BulkProjectMappingResponse,
  EnabledModelItem,
} from "../common/ObjectTypes";
import {
  aiMappingsRowActions,
  buildBulkExcludeLabels,
  buildBulkMappingLabels,
  buildBulkResetLabels,
  matchesSearch,
  ModelFilter,
} from "./aiMappingsHelpers";

type AiMappingsLabelTableProps = {
  projectId: number;
  /** The container's current model filter — a specific model_id, or the
   *  "All models" sentinel. Passed through to EditMappingModal, which needs
   *  it to decide whether add-mode can prefill an implicit model or must ask
   *  (AKL-729 §4). */
  modelFilter: ModelFilter;
  /** Every model enabled for this project — options for EditMappingModal's
   *  add-mode model selector when `modelFilter` is "All models". */
  models: EnabledModelItem[];
  /** model_id → display name/status/disabled-state, for the new Model column
   *  and its inactive/disabled badges (AKL-729, disable follow-up). */
  modelNamesById: Record<
    number,
    { name: string; status: string; disabledAt: string | null }
  >;
  rows: AiMappingsRow[];
  loading: boolean;
  canManage: boolean;
  /** Refetch mappings and the species list after any write. */
  onChanged: () => void;
};

type FilterKey = "all" | "in" | "excluded" | "customized" | "manual";

type PendingAction =
  | { type: "exclude"; row: AiMappingsRow }
  | { type: "delete"; row: AiMappingsRow }
  | { type: "removeSpecies"; row: AiMappingsRow }
  | { type: "bulkExclude"; count: number }
  | { type: "bulkReset"; count: number };

type EditState = {
  open: boolean;
  mode: EditMappingMode;
  row: AiMappingsRow | null;
};

/**
 * Narrow a caught value to the normalized error `src/api/axios.tsx`'s response
 * interceptor rejects with for a 4xx it handled (`{ message, status, data }`).
 * Returns null for anything else — a bare `Error`, a thrown string, a
 * non-object — so a caller only toasts a message the API actually sent.
 *
 * This exists because the interceptor does **not** toast a 4xx: it normalizes
 * and rejects, leaving the toast to the caller (only `ECONNABORTED` and
 * `ERR_NETWORK` toast from inside it; a 401 throws `UnauthorizedError` and
 * 5xx redirects; a 403 is normalized like any other 4xx since AKL-939). Kept
 * local to this file deliberately — the rest of the repo
 * still uses `catch (error: any)`, and this is not yet a shared utility.
 * @param error the caught value
 * @returns the normalized status/message, or null if it isn't one
 */
const asNormalizedApiError = (
  error: unknown,
): { status: number; message: string } | null => {
  if (typeof error !== "object" || error === null) return null;
  const { status, message } = error as { status?: unknown; message?: unknown };
  return typeof status === "number" && typeof message === "string"
    ? { status, message }
    : null;
};

/**
 * The label-first AI Mappings table (AKL-715, combined view since AKL-729):
 * one row per raw model label — spanning every enabled model at once by
 * default, or scoped to a single one when `modelFilter` narrows it —
 * interleaved with manual-only species rows, with search, filter chips,
 * multi-select bulk actions, and per-row edit / exclude / reset /
 * species-list actions. Each row carries its own `ai_model_id`, so every
 * write action derives the model from the row itself rather than a single
 * container-level model, which is what makes this safe under a mixed-model
 * row set. Presentational — all fetch/refetch state lives in the parent
 * (`ManageProjectAiMappings`); this component only owns local UI state
 * (search, filters, selection, which modal is open).
 */
const AiMappingsLabelTable: React.FC<AiMappingsLabelTableProps> = ({
  projectId,
  modelFilter,
  models,
  modelNamesById,
  rows,
  loading,
  canManage,
  onChanged,
}) => {
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(pageSize);

  const [editState, setEditState] = useState<EditState>({
    open: false,
    mode: "add",
    row: null,
  });
  const [addSpeciesState, setAddSpeciesState] = useState<{
    open: boolean;
    taxonId: number | null;
    taxonLabel: string | null;
  }>({ open: false, taxonId: null, taxonLabel: null });

  const [pending, setPending] = useState<PendingAction | null>(null);
  const [confirmSaving, setConfirmSaving] = useState(false);

  const [bulkSaving, setBulkSaving] = useState(false);
  const [repointOpen, setRepointOpen] = useState(false);
  const [repointTaxonId, setRepointTaxonId] = useState<number | null>(null);

  // Drop any selected rowIds that no longer exist in `rows` (e.g. after a
  // delete/remove or an external refetch) so "N selected" and the
  // hidden-by-filter count never include phantom rows.
  useEffect(() => {
    setSelected((prev) => {
      const valid = new Set(rows.map((row) => row.rowId));
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (valid.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [rows]);

  const filteredRows = useMemo(() => {
    const searched = rows
      .map((row) => matchesSearch(row, searchQuery))
      .filter((row): row is AiMappingsRow => row != null);
    switch (activeFilter) {
      case "in":
        return searched.filter((row) => row.target_kind !== "excluded");
      case "excluded":
        return searched.filter((row) => row.target_kind === "excluded");
      case "customized":
        return searched.filter((row) => row.customized);
      case "manual":
        return searched.filter((row) => row.manual);
      default:
        return searched;
    }
  }, [rows, searchQuery, activeFilter]);

  // Project-wide status per taxon_id across every row currently in view
  // (every enabled model when filtered to "All models", or just one when
  // narrowed — informational only, drives the taxon picker's warning when a
  // picked species is excluded via some *other* row; never mutates this
  // row's own target_kind, since exclusion is per-raw-label here, not
  // per-species).
  const taxonStatus = useMemo(() => {
    const map = new Map<number, "in" | "excluded">();
    rows.forEach((row) => {
      if (row.taxon_id == null) return;
      // Several raw labels can resolve to the same taxon with different
      // statuses — "excluded" must win regardless of row order, since this
      // map drives the "excluded elsewhere" warning and a later "in" row
      // must never silently clear that signal.
      if (row.target_kind === "excluded") {
        map.set(row.taxon_id, "excluded");
      } else if (!map.has(row.taxon_id)) {
        map.set(row.taxon_id, "in");
      }
    });
    return map;
  }, [rows]);

  // Taxa some row currently in view already has a raw-label mapping for —
  // used by AddSpeciesModal to explain whether a newly-added species will
  // show up as a normal (label-backed) row or a manual-only one. Spans every
  // enabled model when filtered to "All models".
  const labelTaxonIds = useMemo(() => {
    const set = new Set<number>();
    rows.forEach((row) => {
      if (row.raw_label != null && row.taxon_id != null) set.add(row.taxon_id);
    });
    return set;
  }, [rows]);

  // How many *other* label rows resolve to the same species — several raw
  // labels can share one target, and editing/excluding one doesn't touch the
  // others (label-keyed, not species-keyed; see EditMappingModal's Status
  // helper text). Surfaced so that's not a silent surprise.
  const taxonLabelCounts = useMemo(() => {
    const counts = new Map<number, number>();
    rows.forEach((row) => {
      if (row.raw_label == null || row.taxon_id == null) return;
      counts.set(row.taxon_id, (counts.get(row.taxon_id) ?? 0) + 1);
    });
    return counts;
  }, [rows]);

  const sourceNoteFor = (row: AiMappingsRow): string => {
    if (row.manual) return t("manual — no model label");
    if (row.target_kind === "excluded") return t("excluded for this project");
    // `row.source` (not `customized`) is the real signal: a project-scope row
    // is a project override whether or not a display name was also set on
    // it — including one created under a *different* model that happens to
    // shadow this model's own default for the same raw label. Keying this off
    // `customized` instead would misreport it as "model default".
    if (row.source === "project") return t("project override");
    return t("model default");
  };

  // Reset to page 1 whenever the filtered set changes shape (a new search term
  // or filter chip), so the view never lands on a now-out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [searchQuery, activeFilter]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / perPage));
  // Clamp rather than reset on every row-count change (e.g. after a delete) —
  // only when the current page has actually fallen out of range.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  // Only the current page's rows are rendered — search, filters, stats, and
  // bulk-select-all below all still operate over the full `filteredRows` set,
  // this is purely a render-cost cap for large label catalogues.
  const pagedRows = useMemo(
    () => filteredRows.slice((page - 1) * perPage, page * perPage),
    [filteredRows, page, perPage],
  );

  const stats = useMemo(
    () => ({
      total: rows.length,
      inList: rows.filter((row) => row.target_kind !== "excluded").length,
      excluded: rows.filter((row) => row.target_kind === "excluded").length,
      customized: rows.filter((row) => row.customized).length,
    }),
    [rows],
  );

  const selectedRows = rows.filter((row) => selected.has(row.rowId));
  // Selection persists across search/filter changes (a deliberate choice — an
  // admin building up a selection shouldn't lose it by refining the view),
  // but bulk actions then act on rows the current view may be hiding. Surface
  // that count rather than letting it act silently.
  const hiddenSelectedCount =
    selected.size -
    filteredRows.filter((row) => selected.has(row.rowId)).length;

  const toggleRow = (rowId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  // Only ever adds/removes the currently *visible* (filtered) rows — must
  // never touch a selection made under a different search/filter, or toggling
  // "select all" after narrowing the view would silently wipe or overwrite it.
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const allVisible =
        filteredRows.length > 0 &&
        filteredRows.every((row) => prev.has(row.rowId));
      const next = new Set(prev);
      if (allVisible) {
        filteredRows.forEach((row) => next.delete(row.rowId));
      } else {
        filteredRows.forEach((row) => next.add(row.rowId));
      }
      return next;
    });
  };

  const openAddCustom = () =>
    setEditState({ open: true, mode: "add", row: null });
  const openEdit = (row: AiMappingsRow) =>
    setEditState({ open: true, mode: "edit", row });
  const openAddOverride = (row: AiMappingsRow) =>
    setEditState({ open: true, mode: "addOverride", row });
  const closeEdit = () => setEditState({ open: false, mode: "add", row: null });

  const openAddSpecies = (row?: AiMappingsRow) =>
    setAddSpeciesState({
      open: true,
      taxonId: row?.taxon_id ?? null,
      taxonLabel: row?.resolved_display_name ?? null,
    });
  const closeAddSpecies = () =>
    setAddSpeciesState({ open: false, taxonId: null, taxonLabel: null });

  const runPending = async () => {
    if (!pending || confirmSaving) return;
    setConfirmSaving(true);
    try {
      if (pending.type === "bulkExclude") {
        // `buildBulkExcludeLabels` drops already-excluded rows (never sent, so
        // never counted as failures) and manual rows, which have no label to
        // attach a mapping to. The button resolved the same set to size its
        // confirmation, which is why the predicate lives in one helper rather
        // than being spelled out at both call sites. Every entry carries its
        // own ai_model_id — required under the combined view, where selected
        // rows can come from different models at once.
        const labels = buildBulkExcludeLabels(selectedRows);
        // No `taxon_id` for an excluded upsert (the API rejects the pair with
        // a 422), and no `display_name_override` — the server leaves it
        // untouched for an exclusion, matching the single-row call below.
        const summary =
          labels.length === 0
            ? null
            : await bulkUpsertProjectMappings(projectId, {
                target_kind: "excluded",
                labels,
              });
        setPending(null);
        reportBulkResult(
          summary,
          t("Selected labels excluded"),
          "{{failed}} of the selected labels could not be excluded",
          bulkExcludeEmptyMessage,
        );
        return;
      }
      if (pending.type === "bulkReset") {
        // `buildBulkResetLabels` is the pre-filter here: only a project
        // override with a global default behind it can be reset. A custom
        // mapping has nothing to fall back to (the per-row UI hides Reset for
        // those, and the API skips them), and a manual row has no label at all.
        const labels = buildBulkResetLabels(selectedRows);
        const summary =
          labels.length === 0
            ? null
            : await bulkResetProjectMappings(projectId, { labels });
        setPending(null);
        reportBulkResult(
          summary,
          t("Selected labels reset to model default"),
          "{{failed}} of the selected labels could not be reset",
          bulkResetEmptyMessage,
        );
        return;
      }
      const { type, row } = pending;
      if (type === "delete" && row.mapping_id != null) {
        await deleteProjectMapping(projectId, row.mapping_id);
        toast.success(t("Mapping deleted"));
      } else if (type === "exclude" && row.raw_label != null) {
        if (row.source === "project" && row.mapping_id != null) {
          await updateProjectMapping(projectId, row.mapping_id, {
            target_kind: "excluded",
          });
        } else {
          await addProjectMapping(projectId, {
            // The row's own model, not a single container-level one — see
            // the bulkExclude branch above for why.
            ai_model_id: row.ai_model_id as number,
            raw_label: row.raw_label,
            target_kind: "excluded",
          });
        }
        toast.success(t('"{{label}}" excluded', { label: row.raw_label }));
      } else if (type === "removeSpecies" && row.species_list_item_id != null) {
        await removeSpeciesListItem(projectId, row.species_list_item_id);
        toast.success(t("Species removed"));
      }
      setPending(null);
      onChanged();
    } catch (error: unknown) {
      console.error("Error applying row action:", error);
      const apiError = asNormalizedApiError(error);
      if (apiError) {
        toast.error(apiError.message);
      }
    } finally {
      setConfirmSaving(false);
    }
  };

  const pendingMessage = (): string => {
    if (!pending) return "";
    if (pending.type === "delete") return t("Delete this mapping?");
    if (pending.type === "removeSpecies")
      return t("Remove this species from the list?");
    if (pending.type === "bulkExclude")
      return t(
        "Exclude {{count}} selected labels? Predictions of them will stop surfacing.",
        { count: pending.count },
      );
    if (pending.type === "bulkReset")
      return t(
        "Reset {{count}} selected labels to their model defaults? Project overrides for them will be removed, and any excluded labels will be included again — their species will appear on the project species list.",
        { count: pending.count },
      );
    return t('Exclude "{{label}}"? Predictions of it will stop surfacing.', {
      label: pending.row.raw_label,
    });
  };

  const pendingConfirmLabel = (): string => {
    if (pending?.type === "delete") return t("Delete");
    if (pending?.type === "removeSpecies") return t("Remove");
    if (pending?.type === "bulkReset") return t("Reset");
    return t("Exclude");
  };

  // Delete/Remove permanently drop a row rather than just toggling its
  // status (Exclude is reversible from the same table) — rendered with
  // ConfirmationModal's `danger` treatment (orange icon badge + btnDanger)
  // rather than the default primary confirm button. Bulk reset joins them:
  // it deletes project overrides outright, and unlike Exclude there is no
  // undo from this table.
  const pendingIsDestructive = (): boolean =>
    pending?.type === "delete" ||
    pending?.type === "removeSpecies" ||
    pending?.type === "bulkReset";

  /**
   * A bulk action whose pre-filter left nothing to send: say why, and clear the
   * selection. No refetch — nothing changed server-side (AKL-751 round 2).
   *
   * Shared by `reportBulkResult`'s `null` branch and by the bulk-reset button,
   * which resolves its actionable count before opening the confirmation and so
   * discovers an empty selection without ever making a request.
   * @param message the already-translated explanation
   */
  const reportNothingActionable = (message: string) => {
    toast.info(message);
    setSelected(new Set());
  };

  // Used from two places (the button's own empty check and `runPending`), so
  // the wording cannot drift between them. It names all three reasons a row can
  // be unresettable, because each needs a different remedy: a row already at its
  // model default needs nothing; a label the project added itself has no default
  // to fall back on and is removed with Delete; and a manually added species is
  // not a label mapping at all, so it is taken off the list with Remove.
  const bulkResetEmptyMessage = t(
    "None of the selected labels can be reset — they are already at their model default, or they are entries you added yourself: a label you added is removed with Delete, and a manually added species with Remove.",
  );

  // The exclude pre-filter drops two different kinds of row, so the message
  // names both: rows already excluded, and manual species rows, which have no
  // model label to exclude in the first place.
  const bulkExcludeEmptyMessage = t(
    "None of the selected labels can be excluded — they are already excluded, or they are manually added species with no model label to exclude.",
  );

  /**
   * Report a bulk endpoint's summary response (AKL-751): each bulk action is
   * now one request whose response accounts for every row, so there is no
   * partial client-side state to reconcile — the server applied every valid
   * row in one transaction and told us what it skipped.
   *
   * Always clears the selection, and refetches whenever a request was actually
   * made — success or partial failure alike. A `null` summary means nothing was
   * sent, so there is nothing to refetch — but it is no longer silent (AKL-751
   * round 2): an action that quietly did nothing is indistinguishable from a
   * broken button, so the `null` branch says why via `emptyMessage`.
   *
   * Unlike the settled-batch reporter this replaces, a non-empty `skipped`
   * always toasts: an all-skipped response is still a 200, so the axios
   * interceptor never fires for it and staying silent would hide the failure
   * entirely. `unchanged_count` gets no toast of its own — a row already in
   * the requested state has reached the desired end state, which is success.
   * @param summary the endpoint's summary, or null when nothing was sent
   * @param successMessage the already-translated success toast
   * @param partialFailureKey the partial-failure toast's translation key,
   *   translated here (not by the caller) so `failed` is interpolated by
   *   i18next itself instead of a post-hoc string .replace()
   * @param emptyMessage the already-translated message explaining why nothing
   *   was sent, shown when `summary` is null
   */
  const reportBulkResult = (
    summary: BulkProjectMappingResponse | null,
    successMessage: string,
    partialFailureKey: string,
    emptyMessage: string,
  ) => {
    if (summary == null) {
      reportNothingActionable(emptyMessage);
      return;
    }
    const failed = summary.skipped.length;
    if (failed === 0) {
      toast.success(successMessage);
    } else {
      toast.warning(t(partialFailureKey, { failed }));
      // Logged as one array rather than per entry -- the reason codes are only
      // ever diagnostic here.
      console.error("Bulk action skipped entries:", summary.skipped);
    }
    setSelected(new Set());
    onChanged();
  };

  const runRepoint = async () => {
    if (bulkSaving || repointTaxonId == null) return;
    setBulkSaving(true);
    try {
      // `buildBulkMappingLabels` is the pre-filter here: manual rows with no
      // raw label have nothing to attach a mapping to. The server clears each
      // row's display_name_override for a taxon upsert, so no stale name
      // carries over to the new species.
      const labels = buildBulkMappingLabels(selectedRows);
      const summary =
        labels.length === 0
          ? null
          : await bulkUpsertProjectMappings(projectId, {
              target_kind: "taxon",
              taxon_id: repointTaxonId,
              labels,
            });
      setRepointOpen(false);
      setRepointTaxonId(null);
      reportBulkResult(
        summary,
        t("Selected labels repointed"),
        "{{failed}} of the selected labels could not be repointed",
        t(
          "None of the selected labels can be repointed — a manually added species has no model label to map.",
        ),
      );
    } catch (error: unknown) {
      // The axios interceptor normalizes a 4xx and rejects without toasting,
      // so surface it here (see `runPending`). The modal stays open with
      // the picked taxon intact so the repoint can be retried.
      console.error("Error bulk-repointing labels:", error);
      const apiError = asNormalizedApiError(error);
      if (apiError) {
        toast.error(apiError.message);
      }
    } finally {
      setBulkSaving(false);
    }
  };

  const filterChips: { key: FilterKey; label: string }[] = [
    { key: "all", label: t("All labels") },
    { key: "in", label: t("In species list") },
    { key: "excluded", label: t("Excluded") },
    { key: "customized", label: t("Customized") },
    { key: "manual", label: t("Manual / not model-predicted") },
  ];

  const allVisibleSelected =
    filteredRows.length > 0 &&
    filteredRows.every((row) => selected.has(row.rowId));

  return (
    <div className={tailwindClassnames.formSectionCard}>
      <div className="flex flex-row items-center justify-between gap-4 mb-1 flex-wrap">
        <h2 className={`${tailwindClassnames.basicSubHeading} !mt-0 !mb-0`}>
          {t("Model Mappings and Project Species List")}
        </h2>
        <div className="flex flex-row gap-2 shrink-0">
          {canManage && !loading && (
            <button
              type="button"
              className={`${tailwindClassnames.addButton} inline-flex items-center gap-1.5`}
              onClick={() => openAddSpecies()}
              data-testid="add-species-button"
            >
              {t("Add species")}
              <Tooltip
                title={t(
                  "For a taxon this model can't predict at all — adds it to the species list for manual tagging only, with no effect on AI prediction resolution.",
                )}
                placement="top"
                arrow
                slotProps={{
                  tooltip: { sx: { fontSize: "0.95em", maxWidth: 320 } },
                }}
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 600 }}
              >
                <span
                  className="inline-flex items-center leading-none scale-150"
                  onClick={(e) => e.stopPropagation()}
                  aria-hidden="true"
                >
                  <InfoIcon
                    size={15}
                    className="text-storm-blue leading-none"
                    style={{ display: "block" }}
                  />
                </span>
              </Tooltip>
            </button>
          )}
          {canManage && !loading && models.length > 0 && (
            <button
              type="button"
              className={`${tailwindClassnames.addButton} inline-flex items-center gap-1.5`}
              onClick={openAddCustom}
              data-testid="add-mapping-button"
            >
              {t("Add custom mapping")}
              <Tooltip
                title={t(
                  "For mapping a model's raw label to a species when a default mapping does not exist",
                )}
                placement="top"
                arrow
                slotProps={{
                  tooltip: { sx: { fontSize: "0.95em", maxWidth: 320 } },
                }}
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 600 }}
              >
                <span
                  className="inline-flex items-center leading-none scale-150"
                  onClick={(e) => e.stopPropagation()}
                  aria-hidden="true"
                >
                  <InfoIcon
                    size={15}
                    className="text-storm-blue leading-none"
                    style={{ display: "block" }}
                  />
                </span>
              </Tooltip>
            </button>
          )}
        </div>
      </div>

      <p className={`${tailwindClassnames.helperText} mb-3`}>
        {t(
          "The labels for all enabled models. A label is either in the species list (reviewers see and can tag it) or excluded (its predictions never surface).",
        )}
      </p>

      {!loading && rows.length > 0 && (
        <div className="flex flex-row flex-wrap gap-4 items-baseline mb-3 text-sm">
          <span className="text-primary-off-black dark:text-primary-off-white font-medium">
            {stats.total} {t("mappings")}
          </span>
          <span className="text-primary-off-black dark:text-primary-off-white">
            {stats.inList} {t("in species list")}
          </span>
          <span className="text-greyed-text">
            {stats.excluded} {t("excluded")}
          </span>
          <span className="text-greyed-text">
            {stats.customized} {t("customized")}
          </span>
        </div>
      )}

      {rows.length > 0 && (
        <div className="flex flex-col gap-1 mb-3">
          <div className="relative">
            <input
              type="text"
              className={`${tailwindClassnames.detailsPageTextInput} pr-20`}
              placeholder={t(
                "Search raw label, species, or the name reviewers see...",
              )}
              aria-label={t(
                "Search raw label, species, or the name reviewers see...",
              )}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="ai-mappings-search"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              {searchQuery !== "" && (
                <button
                  type="button"
                  className="text-greyed-text hover:text-off-black-light dark:hover:text-off-white-dark text-sm leading-none"
                  onClick={() => setSearchQuery("")}
                  aria-label={t("Clear search")}
                >
                  ×
                </button>
              )}
              <span className="text-xs text-greyed-text whitespace-nowrap">
                {searchQuery.trim() !== ""
                  ? t("{{count}} matches", { count: filteredRows.length })
                  : t("{{count}} rows", { count: filteredRows.length })}
              </span>
            </span>
          </div>
          <div className="flex flex-row flex-wrap gap-2">
            {filterChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                className={
                  activeFilter === chip.key
                    ? tailwindClassnames.filterChipActive
                    : tailwindClassnames.filterChipInactive
                }
                onClick={() => setActiveFilter(chip.key)}
                aria-pressed={activeFilter === chip.key}
                data-testid={`filter-chip-${chip.key}`}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {canManage && selected.size > 0 && (
        <div className="flex flex-row flex-wrap items-center gap-4 bg-sky-blue/10 rounded px-4 py-2 mb-3">
          <span className="text-sm font-semibold text-storm-blue dark:text-sky-blue-light">
            {t("{{count}} labels selected", { count: selected.size })}
          </span>
          {hiddenSelectedCount > 0 && (
            <span className="text-xs text-greyed-text">
              {t("({{count}} hidden by the current filter)", {
                count: hiddenSelectedCount,
              })}
            </span>
          )}
          <button
            type="button"
            className="text-sky-blue-dark dark:text-sky-blue hover:underline text-sm font-semibold disabled:opacity-50"
            onClick={() => {
              // The confirmation must state what will actually happen, not how
              // many rows are ticked: `buildBulkResetLabels` keeps only rows
              // with a project override that shadows a model default, and in
              // the all-models combined view most rows are plain model
              // defaults. Interpolating `selected.size` into a *destructive*
              // confirmation would promise 300 resets and deliver 3. Resolved
              // on click rather than per keystroke, so a multi-thousand-row
              // selection costs one pass at the moment it matters.
              const resettable = buildBulkResetLabels(selectedRows).length;
              if (resettable === 0) {
                reportNothingActionable(bulkResetEmptyMessage);
                return;
              }
              setPending({ type: "bulkReset", count: resettable });
            }}
            disabled={bulkSaving}
            data-testid="bulk-reset-button"
          >
            {t("Reset to model default")}
          </button>
          <button
            type="button"
            className="text-primary-orange-dark hover:underline text-sm font-semibold disabled:opacity-50"
            onClick={() => {
              // Same rule as bulk reset below: the confirmation states the rows
              // that will actually change, not the rows that are ticked. The
              // exclude pre-filter drops already-excluded rows and manual rows,
              // so a selection of either would otherwise be confirmed and then
              // do nothing.
              const excludable = buildBulkExcludeLabels(selectedRows).length;
              if (excludable === 0) {
                reportNothingActionable(bulkExcludeEmptyMessage);
                return;
              }
              setPending({ type: "bulkExclude", count: excludable });
            }}
            disabled={bulkSaving}
            data-testid="bulk-exclude-button"
          >
            {t("Exclude")}
          </button>
          <button
            type="button"
            className="text-sky-blue-dark dark:text-sky-blue hover:underline text-sm font-semibold disabled:opacity-50"
            onClick={() => setRepointOpen(true)}
            disabled={bulkSaving}
          >
            {t("Point all at one species…")}
          </button>
          <button
            type="button"
            className="ml-auto text-xs text-greyed-text hover:underline"
            onClick={() => setSelected(new Set())}
          >
            {t("Clear selection")}
          </button>
        </div>
      )}

      {loading ? (
        <p className={tailwindClassnames.basicLabel}>{t("Loading")}...</p>
      ) : rows.length === 0 ? (
        <p className={tailwindClassnames.basicLabel}>
          {/* No model is enabled yet, so "add a custom label" (which needs a
              model to attach it to) doesn't apply — the "Add custom mapping"
              button above is hidden in that case too. */}
          {models.length === 0
            ? t("No mappings yet.")
            : t("No mappings yet. Add a custom label to get started.")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className={tailwindClassnames.tableContainer}>
            <thead className={tailwindClassnames.tableHead}>
              <tr>
                {canManage && (
                  <th className={tailwindClassnames.tableHeadText}>
                    <input
                      type="checkbox"
                      className={tailwindClassnames.checkBox}
                      checked={allVisibleSelected}
                      onChange={toggleAllVisible}
                      aria-label={t("Select all matching rows")}
                    />
                  </th>
                )}
                <th className={tailwindClassnames.tableHeadText}>
                  {t("Raw label")}
                </th>
                <th className={tailwindClassnames.tableHeadText}>
                  {t("Resolves to")}
                </th>
                <th className={tailwindClassnames.tableHeadText}>
                  <span className="inline-flex items-center gap-1">
                    {t("Reviewers see")}
                    <InfoTooltip
                      text={t(
                        "The name reviewers see for this species everywhere in the project.",
                      )}
                    />
                  </span>
                </th>
                <th className={tailwindClassnames.tableHeadText}>
                  {t("Model")}
                </th>
                <th className={tailwindClassnames.tableHeadText}>
                  {t("Status")}
                </th>
                {canManage && (
                  <th className={tailwindClassnames.tableHeadText}>
                    {t("Actions")}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row) => {
                const isLabelRow = row.raw_label != null;
                const isExcluded = row.target_kind === "excluded";
                const actions = aiMappingsRowActions(row);
                const canRemoveSpeciesRow = row.species_list_item_id != null;
                const rowLabel =
                  row.raw_label ?? row.resolved_display_name ?? row.rowId;

                return (
                  <tr key={row.rowId} className={tailwindClassnames.noHover}>
                    {canManage && (
                      <td className={tailwindClassnames.tableBodyText}>
                        <input
                          type="checkbox"
                          className={tailwindClassnames.checkBox}
                          checked={selected.has(row.rowId)}
                          onChange={() => toggleRow(row.rowId)}
                          aria-label={t("Select {{label}}", {
                            label: rowLabel,
                          })}
                        />
                      </td>
                    )}
                    <td
                      className={tailwindClassnames.tableBodyText}
                      title={row.raw_label ?? undefined}
                    >
                      {row.raw_label ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="font-mono">{row.raw_label}</span>
                          {row.matchedField === "raw_label" && (
                            <span className={tailwindClassnames.matchChip}>
                              {t("match")}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="italic text-greyed-text">
                          {t("added by hand")}
                        </span>
                      )}
                    </td>
                    <td
                      className={tailwindClassnames.tableBodyText}
                      title={row.resolved_display_name ?? undefined}
                    >
                      {isExcluded ? (
                        <span className="italic text-greyed-text">
                          {t("Excluded")}
                        </span>
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          <span className="font-semibold">
                            {row.resolved_display_name || ""}
                          </span>
                          {row.scientific_name && (
                            <span className="italic text-xs text-greyed-text">
                              {row.scientific_name}
                            </span>
                          )}
                          {row.matchedField === "species" && (
                            <span className={tailwindClassnames.matchChip}>
                              {t("match")}
                            </span>
                          )}
                          {row.raw_label != null &&
                            row.taxon_id != null &&
                            (taxonLabelCounts.get(row.taxon_id) ?? 0) > 1 && (
                              <span className="text-[10px] text-greyed-text">
                                {t(
                                  "shares name & status with {{count}} other labels",
                                  {
                                    count:
                                      (taxonLabelCounts.get(row.taxon_id) ??
                                        1) - 1,
                                  },
                                )}
                              </span>
                            )}
                        </div>
                      )}
                    </td>
                    <td
                      className={tailwindClassnames.tableBodyText}
                      title={row.display_name_override ?? undefined}
                    >
                      {isExcluded ? (
                        <span className="italic text-greyed-text">
                          {t("nothing — suppressed")}
                        </span>
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          <span>
                            {row.display_name_override ||
                              row.resolved_display_name ||
                              ""}
                          </span>
                          {row.matchedField === "display_name" && (
                            <span className={tailwindClassnames.matchChip}>
                              {t("match")}
                            </span>
                          )}
                          {row.customized && (
                            <span className={tailwindClassnames.customizedChip}>
                              {t("customized")}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td
                      className={tailwindClassnames.tableBodyText}
                      title={
                        row.ai_model_id != null
                          ? modelNamesById[row.ai_model_id]?.name
                          : undefined
                      }
                    >
                      {row.ai_model_id != null ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span>
                            {modelNamesById[row.ai_model_id]?.name ?? "—"}
                          </span>
                          {modelNamesById[row.ai_model_id]?.status != null &&
                            modelNamesById[row.ai_model_id]?.status !==
                              "active" && (
                              <span
                                className={
                                  tailwindClassnames.inactiveModelBadge
                                }
                              >
                                {t("Inactive")}
                              </span>
                            )}
                          {modelNamesById[row.ai_model_id]?.disabledAt !=
                            null && (
                            <span className={tailwindClassnames.chipFailed}>
                              {t("Disabled")}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-greyed-text">—</span>
                      )}
                    </td>
                    <td className={tailwindClassnames.tableBodyText}>
                      {isLabelRow ? (
                        <span className="inline-flex border border-off-white-dark dark:border-off-black-light rounded overflow-hidden">
                          <button
                            type="button"
                            className={
                              !isExcluded
                                ? tailwindClassnames.segmentedControlActive
                                : tailwindClassnames.segmentedControlInactive
                            }
                            disabled={!canManage || !isExcluded}
                            onClick={() =>
                              openEdit({ ...row, target_kind: "taxon" })
                            }
                          >
                            {t("In species list")}
                          </button>
                          <button
                            type="button"
                            className={
                              isExcluded
                                ? tailwindClassnames.segmentedControlActive
                                : tailwindClassnames.segmentedControlInactive
                            }
                            disabled={!canManage || isExcluded}
                            onClick={() => setPending({ type: "exclude", row })}
                          >
                            {t("Excluded")}
                          </button>
                        </span>
                      ) : (
                        <span className="text-xs text-greyed-text">
                          {t("In species list")}
                        </span>
                      )}
                      <span className="block text-[10px] text-greyed-text mt-1">
                        {sourceNoteFor(row)}
                      </span>
                    </td>
                    {canManage && (
                      <td className={tailwindClassnames.tableBodyText}>
                        {isLabelRow ? (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="inline-flex"
                              aria-label={t("Edit mapping")}
                              onClick={() =>
                                actions.canEdit
                                  ? openEdit(row)
                                  : openAddOverride(row)
                              }
                            >
                              <EditPencilIcon />
                            </button>
                            {actions.canDelete && (
                              <button
                                type="button"
                                className="inline-flex"
                                aria-label={t("Delete mapping for {{label}}", {
                                  label: rowLabel,
                                })}
                                onClick={() =>
                                  setPending({ type: "delete", row })
                                }
                              >
                                <DeleteXIcon />
                              </button>
                            )}
                          </div>
                        ) : (
                          canRemoveSpeciesRow && (
                            <button
                              type="button"
                              className="inline-flex"
                              aria-label={t("Remove {{label}}", {
                                label: rowLabel,
                              })}
                              onClick={() =>
                                setPending({ type: "removeSpecies", row })
                              }
                            >
                              <DeleteXIcon />
                            </button>
                          )
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <p className={`${tailwindClassnames.helperText} mt-2 !mb-0`}>
          {t("Showing {{shown}} of {{total}} mappings", {
            shown: filteredRows.length,
            total: rows.length,
          })}
        </p>
      )}

      {!loading && filteredRows.length > 0 && (
        <Pagination
          showText={false}
          handlePageClick={(e: { selected: number }) => setPage(e.selected + 1)}
          pageCount={pageCount}
          resultStart={(page - 1) * perPage + 1}
          resultEnd={Math.min(page * perPage, filteredRows.length)}
          totalCount={filteredRows.length}
          take={perPage}
          loading={loading}
          initialPage={page - 1}
          pageSize={perPage}
          pageSizeOptions={[10, 25, 50]}
          onPageSizeChange={(size) => {
            setPerPage(size);
            setPage(1);
          }}
          mainclassname="flex flex-row items-center justify-between mt-2"
        />
      )}

      <EditMappingModal
        open={editState.open}
        mode={editState.mode}
        row={editState.row}
        projectId={projectId}
        modelFilter={modelFilter}
        models={models}
        taxonStatus={taxonStatus}
        onClose={closeEdit}
        onSaved={onChanged}
      />

      <AddSpeciesModal
        open={addSpeciesState.open}
        projectId={projectId}
        initialTaxonId={addSpeciesState.taxonId}
        initialTaxonLabel={addSpeciesState.taxonLabel}
        taxonStatus={taxonStatus}
        labelTaxonIds={labelTaxonIds}
        onClose={closeAddSpecies}
        onSaved={onChanged}
      />

      <ConfirmationModal
        isOpen={pending != null}
        confirmationMessage={pendingMessage()}
        onConfirm={runPending}
        onCancel={() => setPending(null)}
        confirmLabel={pendingConfirmLabel()}
        saving={confirmSaving}
        danger={pendingIsDestructive()}
      />

      {repointOpen && (
        <div className={`${tailwindClassnames.modalOverlay} z-50`}>
          <div className={`${tailwindClassnames.modalPanel} max-w-3xl`}>
            <div className={tailwindClassnames.modalHeaderRow}>
              <h2 className={tailwindClassnames.modalTitle}>
                {t("Point all at one species")}
              </h2>
              <button
                type="button"
                className={tailwindClassnames.modalCloseButton}
                onClick={() => {
                  setRepointOpen(false);
                  setRepointTaxonId(null);
                }}
                disabled={bulkSaving}
                aria-label={t("Close")}
              >
                &times;
              </button>
            </div>
            <div className={`${tailwindClassnames.modalBody} space-y-4`}>
              <p className={`${tailwindClassnames.helperText} !mt-0`}>
                {t(
                  "{{count}} selected labels will resolve to the species you pick below.",
                  { count: selected.size },
                )}
              </p>
              <ProjectTaxonPicker
                name={t("Taxon")}
                value={repointTaxonId}
                onChange={setRepointTaxonId}
                taxonStatus={taxonStatus}
                cardLabel={t("Species to point these labels to")}
                searchLabel={t("Search the taxonomy")}
              />
            </div>
            <div className={tailwindClassnames.modalFooter}>
              <button
                type="button"
                className={tailwindClassnames.btnSecondary}
                onClick={() => {
                  setRepointOpen(false);
                  setRepointTaxonId(null);
                }}
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                className={tailwindClassnames.btnPrimary}
                onClick={runRepoint}
                disabled={bulkSaving || repointTaxonId == null}
              >
                {t("Save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiMappingsLabelTable;
